import { Users, BarChart2, UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { addDays, parse, isAfter, isBefore, startOfDay } from "date-fns";
import { developmentLog } from "@helpers/developmentLog";
import type { DateRange } from "react-day-picker";
interface PlayerOverviewProps {
  stats?: any;
  activity?: any;
  loading?: boolean;
  error?: string | null;
  dateRange?: DateRange;
  startDate?: Date;
  endDate?: Date;
}

export function PlayerOverview(props: PlayerOverviewProps) {
  const stats = props.stats ?? [];
  const activity = props.activity ?? [];
  const loading = props.loading ?? false;
  const error = props.error ?? null;
  const dateRange = props.dateRange;
  const startDate = props.startDate;
  const endDate = props.endDate;
  // Calculate total unique players
  const totalPlayers = Array.isArray(stats)
    ? new Set(stats.map((p: any) => p.playerId).filter((id: any) => id != null))
        .size
    : 0;

  // Calculate active players (unique, not duplicated, most recent lastPlayedOn within 30 days)
  const today = startOfDay(new Date());
  const thirtyDaysAgo = startOfDay(addDays(today, -30));
  const playerLatestPlayed: Record<string, Date> = {};
  const playerIdCounts: Record<string, number> = {};
  if (Array.isArray(activity)) {
    for (const p of activity) {
      const playerId = p.playerId;
      const lastPlayedOn = p.lastPlayedOn;
      if (!playerId || typeof lastPlayedOn !== "string") continue;
      playerIdCounts[playerId] = (playerIdCounts[playerId] || 0) + 1;
      const played = startOfDay(new Date(lastPlayedOn));
      if (!isNaN(played.getTime())) {
        if (
          !playerLatestPlayed[playerId] ||
          played > playerLatestPlayed[playerId]
        ) {
          playerLatestPlayed[playerId] = played;
        }
      }
    }
  }
  const duplicateIds = Object.entries(playerIdCounts)
    .filter(([_, count]) => count > 1)
    .map(([playerId]) => playerId);
  const activePlayerIdList = Object.entries(playerLatestPlayed)
    .filter(
      ([, played]) =>
        (isAfter(played, thirtyDaysAgo) ||
          played.getTime() === thirtyDaysAgo.getTime()) &&
        (isBefore(played, today) || played.getTime() === today.getTime())
    )
    .map(([playerId]) => playerId);
  const filteredActivePlayerIdList = activePlayerIdList.filter(
    (id) => !duplicateIds.includes(id)
  );
  const activePlayers = filteredActivePlayerIdList.length;

  // Calculate new signups (unique, not duplicated, signupDate in last 30 days)
  let newSignups = 0;
  if (Array.isArray(stats)) {
    // Map to count playerId occurrences and store the earliest valid signupDate
    const signupIdCounts: Record<string, number> = {};
    const signupDates: Record<string, string> = {};
    for (const p of stats) {
      const playerId = p.playerId;
      const signupDate = p.signupDate;
      if (!playerId || typeof signupDate !== "string") continue;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(signupDate)) continue;
      signupIdCounts[playerId] = (signupIdCounts[playerId] || 0) + 1;
      // Always keep the earliest signupDate for this playerId
      if (!signupDates[playerId] || signupDate < signupDates[playerId]) {
        signupDates[playerId] = signupDate;
      }
    }
    // Only count playerIds that appear exactly once (no duplicates) AND are in filteredActivePlayerIdList
    const activeSet = new Set(filteredActivePlayerIdList);
    newSignups = Object.entries(signupDates).filter(([playerId, dateStr]) => {
      if (signupIdCounts[playerId] !== 1) return false;
      if (!activeSet.has(playerId)) return false;
      const date = startOfDay(new Date(dateStr));
      return (
        (isAfter(date, thirtyDaysAgo) ||
          date.getTime() === thirtyDaysAgo.getTime()) &&
        (isBefore(date, today) || date.getTime() === today.getTime())
      );
    }).length;
  }

  // Debug logs
  developmentLog("Active playerIds:", activePlayerIdList);
  developmentLog(
    "Filtered active (non-duplicated) playerIds:",
    filteredActivePlayerIdList.length,
    filteredActivePlayerIdList
  );
  developmentLog("Duplicated playerIds:", duplicateIds);
  developmentLog("New signups:", newSignups);

  const cards = [
    {
      title: "Total Players ",
      value: totalPlayers.toLocaleString(),
      icon: <Users className="h-7 w-7 text-pink-500" />,
      color: "bg-pink-200",
    },
    {
      title: "Active Players",
      value: activePlayers.toLocaleString(),
      icon: <BarChart2 className="h-7 w-7 text-green-500" />,
      color: "bg-green-200",
    },
    {
      title: "New Signups",
      value: newSignups.toLocaleString(),
      icon: <UserPlus className="h-7 w-7 text-orange-500" />,
      color: "bg-orange-100",
    },
  ];

  return (
    <div className="space-y-6 p-0">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
        <h2 className="text-lg font-semibold text-white tracking-tight mb-2 md:mb-0">
          Player Overview
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((card, idx) => (
          <Card
            key={idx}
            className="flex flex-col bg-white border border-gray-800"
          >
            <CardHeader className="flex flex-row items-center gap-4 pb-2">
              <div className={`rounded-lg p-2 ${card.color}`}>{card.icon}</div>
              <CardTitle className="text-base font-medium text-gray-900">
                {card.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-4 flex items-end">
              {loading ? (
                <span className="text-gray-400">Loading...</span>
              ) : error ? (
                <span className="text-red-500">{error}</span>
              ) : (
                <span className="text-3xl font-bold text-gray-900">
                  {card.value}
                </span>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
